<template>
  <UApp>
    <NuxtLoadingIndicator color="var(--color-brand-blue-600)" />

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </UApp>
</template>

<script setup lang="ts">
const runtimeConfig = useRuntimeConfig()
const siteUrl = runtimeConfig.public.appUrl || 'https://ambulancepresent.be'
const route = useRoute()

const routeLabelMap: Record<string, string> = {
  'prestations': 'Prestations',
  'contact': 'Contact',
  'devis': 'Devis',
  'confirmation-devis': 'Confirmation devis',
  'lexique': 'Lexique',
  'mentions-legales': 'Mentions légales',
  'politique-confidentialite': 'Politique de confidentialité',
  'qui-sommes-nous': 'Qui sommes-nous',
  'recrutement-volontaires': 'Recrutement volontaires',
  'formations': 'Formations',
  'formations-grand-public': 'Formations grand public',
  'stages-secouristes-juniors': 'Stages secouristes juniors',
  'checkout-success': 'Paiement formation confirmé',
}

function normalizePath(path: string): string {
  if (!path || path === '/') {
    return '/'
  }

  const cleanPath = path.split('?')[0]?.split('#')[0] ?? '/'
  return cleanPath.endsWith('/') ? cleanPath.slice(0, -1) || '/' : cleanPath
}

function prettifySegment(segment: string): string {
  const knownLabel = routeLabelMap[segment]
  if (knownLabel) {
    return knownLabel
  }

  return segment
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

const breadcrumbSchemaNode = computed(() => {
  const normalizedPath = normalizePath(route.path)

  if (normalizedPath.startsWith('/volontaires')) {
    return null
  }

  const segments = normalizedPath
    .split('/')
    .filter(Boolean)

  const itemListElement = [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Accueil',
      'item': siteUrl,
    },
  ]

  let currentPath = ''
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`
    itemListElement.push({
      '@type': 'ListItem',
      'position': index + 2,
      'name': prettifySegment(segment),
      'item': `${siteUrl}${currentPath}`,
    })
  })

  return {
    '@type': 'BreadcrumbList',
    'itemListElement': itemListElement,
  }
})

useHead({
  htmlAttrs: {
    class: 'scroll-smooth',
  },
  bodyAttrs: {
    class: 'antialiased scroll-smooth',
  },
  // Manifeste : rend l'espace volontaires installable (obligatoire pour le push sur iPhone)
  link: [
    { key: 'manifest', rel: 'manifest', href: '/site.webmanifest' },
  ],
  meta: [
    { name: 'keywords', content: 'Ambulance Présent 2.0, secours préventif Belgique, dispositif médical préventif Belgique, poste de secours événementiel Hainaut, poste de secours événementiel Liège, poste de secours événementiel Luxembourg, sécurité médicale événementielle Wallonie, ambulance sur événement Wallonie, ambulance de dispositif préventif Belgique, VIM événementiel, devis secours événementiel, devenir secouriste volontaire Belgique, volontariat ambulancier Hainaut, volontariat ambulancier Liège, volontariat médical événementiel' },
  ],
})

useSeoMeta({
  title: 'Ambulance Présent',
  titleTemplate: title => (title !== 'Ambulance Présent' ? `${title} · Ambulance Présent` : title),
  viewport: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
  charset: 'utf-8',
})

// @ts-expect-error -- le getter réactif est résolu à l'exécution par unhead, mais absent de son typage
useSchemaOrg(() => {
  const schemaNodes: any[] = [
    defineWebPage(),
    defineWebSite({
      name: 'Ambulance Présent 2.0 ASBL',
      inLanguage: 'fr-BE',
      url: siteUrl,
    }),
    defineOrganization({
      '@id': `${siteUrl}#organization`,
      'name': 'Ambulance Présent 2.0 ASBL',
      'url': siteUrl,
      'description': 'Ambulance Présent 2.0 ASBL assure des dispositifs médicaux préventifs pour les événements en Belgique, avec équipes secouristes, ambulances et coordination opérationnelle sur mesure.',
      'logo': `${siteUrl}/images/logo.png`,
      'email': 'info@ambulancepresentasbl.be',
      'telephone': '+32474035548',
      'areaServed': ['Belgique', 'Wallonie', 'Bruxelles', 'Hainaut', 'Liège', 'Luxembourg'],
      'knowsAbout': [
        'Secours préventif événementiel',
        'Dispositif médical préventif',
        'Poste de secours événementiel',
        'Ambulance sur dispositif médical préventif événementiel (hors transport de patients)',
        'Véhicule d\'intervention médicalisé (VIM)',
      ],
      'sameAs': [
        'https://www.facebook.com/AmbulancePresent/',
        'https://www.instagram.com/ambulancepresent/',
        'https://www.tiktok.com/@ambulancepresent',
      ],
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': '4ième Rue 33',
        'postalCode': '6040',
        'addressLocality': 'Charleroi',
        'addressCountry': 'BE',
      },
      'contactPoint': [
        {
          email: 'info@ambulancepresentasbl.be',
          telephone: '+32474035548',
          contactType: 'customer service',
          availableLanguage: ['fr', 'en'],
        },
      ],
    }),
  ]

  if (breadcrumbSchemaNode.value) {
    schemaNodes.push(breadcrumbSchemaNode.value)
  }

  return schemaNodes
})
</script>
